import React from "react";
import { useNavigate } from "react-router-dom";
import { WebPaths } from "../../../../routes/data";
import Button from "../../components/ui/Button";
import { motion } from "framer-motion";
import {
  FaArrowRight,
  FaCheck,
  FaShieldHalved,
  FaBolt,
  FaWandMagicSparkles,
  FaFileCircleCheck,
} from "react-icons/fa6";

const UnifiedBottomCTA = () => {
  const navigate = useNavigate();

  return (
    <section className="bg-white py-24 sm:py-32">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="relative overflow-hidden bg-white-f9 border border-gray-100 rounded-3xl shadow-sm lg:grid lg:grid-cols-2 lg:gap-4 p-8 sm:p-12 lg:p-0">
          {/* Left: The "Success Stack" Visual */}
          <div className="relative h-[400px] lg:h-auto min-h-[400px] flex items-center justify-center lg:py-12 order-last lg:order-first">
            {/* Background Glow */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] bg-blue-100/60 rounded-full blur-3xl" />

            {/* Main Card: Application Status */}
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.6 }}
              className="relative z-10 bg-white w-[280px] rounded-2xl shadow-xl border border-gray-100 p-6 flex flex-col gap-4"
            >
              <div className="flex items-center gap-3 border-b border-gray-50 pb-4">
                <div className="h-10 w-10 bg-blue-00 rounded-full flex items-center justify-center text-white text-lg font-bold">
                  SV
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">
                    Application Status
                  </p>
                  <p className="text-xs text-gray-500">Updated just now</p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border border-green-100">
                  <span className="text-sm font-medium text-green-800">
                    Eligibility
                  </span>
                  <FaCheck className="text-green-600" />
                </div>
                <div className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-100">
                  <span className="text-sm font-medium text-blue-800">
                    Review
                  </span>
                  <span className="text-xs font-bold bg-blue-200 text-blue-800 px-2 py-0.5 rounded-full">
                    In Progress
                  </span>
                </div>
              </div>

              <div className="mt-2 text-center">
                <p className="text-xs text-gray-400 font-medium">
                  Estimated Approval: High
                </p>
              </div>
            </motion.div>

            {/* Floating Chip 1: FiBi Score */}
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ repeat: Infinity, duration: 4, ease: "easeInOut" }}
              className="absolute top-12 lg:top-20 right-8 lg:right-16 z-20 bg-gray-900 text-white p-3 rounded-2xl shadow-lg border border-gray-700 flex items-center gap-3 pr-5"
            >
              <div className="h-8 w-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <FaWandMagicSparkles className="text-white text-xs" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase font-bold">
                  FiBi Score
                </p>
                <p className="text-sm font-bold text-purple-200">98/100</p>
              </div>
            </motion.div>

            {/* Floating Chip 2: Documents Verified */}
            <motion.div
              animate={{ y: [10, -10, 10] }}
              transition={{
                repeat: Infinity,
                duration: 5,
                ease: "easeInOut",
                delay: 1,
              }}
              className="absolute bottom-12 lg:bottom-24 left-4 lg:left-12 z-20 bg-white p-3 rounded-2xl shadow-lg border border-gray-100 flex items-center gap-3 pr-5"
            >
              <div className="h-8 w-8 bg-green-100 rounded-lg flex items-center justify-center">
                <FaFileCircleCheck className="text-green-600" />
              </div>
              <div>
                <p className="text-[10px] text-gray-400 uppercase font-bold">
                  Documents
                </p>
                <p className="text-sm font-bold text-green-700">All Verified</p>
              </div>
            </motion.div>
          </div>

          {/* Right: The Text & Enhanced Points */}
          <div className="flex flex-col justify-center lg:py-24 lg:pr-24 lg:order-last order-first pb-12">
            <h2 className="text-3xl font-light tracking-tight text-gray-900 sm:text-4xl font-spartan leading-tight">
              A{" "}
              <span className="text-blue-600 italic mx-1">smarter</span>{" "}
              way to
              <br className="hidden sm:block" />
              <span className="text-purple-600 italic mx-1">navigate</span>{" "}
              the world
            </h2>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              One platform for every step of your journey. Whether you're a
              student seeking knowledge, a business expanding borders, or a
              traveler exploring new horizons - we bring it all together.
            </p>

            {/* Enhanced Feature Cards */}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start gap-3 hover:border-blue-200 transition-colors">
                <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
                  <FaShieldHalved />
                </div>
                <span className="font-bold text-gray-900 text-sm">
                  Secure & Private
                </span>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start gap-3 hover:border-purple-200 transition-colors">
                <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
                  <FaBolt />
                </div>
                <span className="font-bold text-gray-900 text-sm">
                  AI Accelerated
                </span>
              </div>

              <div className="bg-white p-4 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-start gap-3 hover:border-green-200 transition-colors">
                <div className="p-2 bg-green-50 text-green-600 rounded-lg">
                  <FaCheck />
                </div>
                <span className="font-bold text-gray-900 text-sm">
                  Expert Verified
                </span>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-x-6">
              <Button
                label="Get Started"
                onClick={() => navigate(WebPaths.Join)}
                variant="primary"
                size="large"
                endIcon={<FaArrowRight />}
                iconHoverStyle="scale"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default UnifiedBottomCTA;
